import {
    Component,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    EnvironmentInjector
} from '@angular/core';
import {Router} from '@angular/router';
import {
    HankoService, LoginCheckApiResponse,
    tenantNeedsTrustedUser,
    userHasMfaEnabled,
    userHasMicrosoftLinked,
} from '../hanko';
import {
    Action,
    AnyState,
    ContinueWithLoginIdentifierInputs,
    ExchangeTokenInputs,
    FlowError,
    Input,
    OTPCodeInputs,
    PasswordInputs,
    RegisterPasswordInputs,
    PasswordRecoveryInputs,
    State,
    ThirdpartyOauthInputs,
    User,
    VerifyPasscodeInputs,
} from '@teamhanko/hanko-elements';
import {Capacitor, PluginListenerHandle} from '@capacitor/core';
import {Clipboard} from '@capacitor/clipboard';
import {App} from '@capacitor/app';
import {Preferences} from '@capacitor/preferences';
import {
    DefaultSystemBrowserOptions,
    InAppBrowser,
} from '@capacitor/inappbrowser';
import {HANKO_OPTIONS} from '../hanko/inj-token';
import {lastValueFrom} from 'rxjs';

type CleanupFunc = () => void;

@Component({
    selector: 'hanko-login-page',
    templateUrl: './hanko-login.page.html',
    styleUrls: ['./hanko-login.page.scss'],
})
export class HankoLoginPage implements OnInit, OnDestroy {

    get emailInvalid() {
        return this.emailError !== null;
    }

    get passwordInvalid() {
        return this.passwordError !== null;
    }

    get passcodeInvalid() {
        return this.passcodeError !== null;
    }

    get otpCodeInvalid() {
        return this.otpCodeError !== null;
    }

    get passwordCreationInvalid() {
        return this.passwordCreationError !== null;
    }

    get mfaOtpCodeInvalid() {
        return this.mfaOtpCodeError !== null;
    }

    showDebugOutput = false;

    isLoggedIn = false;
    isLoading = false;
    isStateChangeInProgress = false;
    showMfaHelp = true;
    stateName: string | null = null;
    stateObject: unknown | null = null;
    error: FlowError | null = null;

    loginIdentifierInput: Input<string> | null = null;
    passwordInput: Input<string> | null = null;
    newPasswordInput: Input<string> | null = null;
    otpCodeInput: Input<string> | null = null;
    passcodeInput: Input<string> | null = null;
    mfaOtpCodeInput: Input<string> | null = null;

    formLoginIdentifier: string | null = '';

    emailTouched = false;
    emailError: 'required' | 'pattern' | null = null;

    passwordTouched = false;
    passwordError: 'required' | null = null;

    passwordCreationTouched = false;
    passwordCreationError: 'required' | 'pattern' | 'length' | null = null;
    passwordCreationRules = {
        minLength: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    };

    passcodeTouched = false;
    passcodeError: 'required' | null = null;
    passcodeResendAfter: number | null = null;
    private passcodeResendCountdownInterval: any = null;

    otpCodeTouched = false;
    otpCodeError: 'required' | null = null;

    mfaOtpCodeTouched = false;
    mfaOtpCodeError: 'required' | null = null;

    formPasscode: string | null = '';
    formPassword: string | null = '';
    formOtpCode: string | null = '';

    loginMethodChooserOptions: {
        passwordEnabled: boolean;
        passcodeEnabled?: boolean;
        backEnabled: boolean;
    } | null = null;
    passcodeConfirmationsOptions: {
        resendEnabled: boolean;
        backEnabled: boolean;
    } | null = null;
    loginInitOptions: {
        rememberMe: boolean;
        continueWithLoginIdentifierEnabled: boolean;
        microsoftEnabled: boolean;
    } | null = null;
    loginPasswordOptions: {
        chooserEnabled: boolean;
        backEnabled: boolean;
    } | null = null;
    loginOtpOptions: {
        securityKeyEnabled: boolean;
        codeValidateEnabled: boolean;
    } | null = null;
    deviceTrustOptions: {
        backEnabled: boolean;
        skipEnabled: boolean;
        trustDeviceEnabled: boolean;
    } | null = null;
    mfaMethodChooserOptions: {
        backEnabled: boolean;
        skipEnabled: boolean;
        continueToOtpSecretEnabled: boolean;
    } | null = null;
    mfaSecretCreationOptions: {
        backEnabled: boolean;
        continueToCodeVerifyEnabled: boolean;
        otp_image_source?: string;
        otp_secret?: string;
    } | null = null;
    createPasswordOptions: {
        backEnabled: boolean;
        skipEnabled: boolean;
        registerPasswordEnabled: boolean;
    } | null = null;
    successOptions: {
        userMfaCreationRequired: boolean;
    } | null = null;

    user: User | null = null;
    thirdPartyBrowserUrl: string | null = null;
    thirdPartyRedirectUrl: string | null = null;

    browserClosed = false;
    browserClosedByUser = true;

    private entraInfo: {
        use_entra_id: boolean;
        entra_id_domain?: string;
    } | null = null;
    private entraUser: string | null = null;

    private readonly hanko = inject(HankoService);
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private continueWithLoginIdentifierAction: Action<ContinueWithLoginIdentifierInputs> | null =
        null;
    private backAction: Action<null> | null = null;
    private skipAction: Action<null> | null = null;
    private continueToLoginMethodChooserAction: Action<null> | null = null;
    private continueToPasswordLoginAction: Action<null> | null = null;
    private continueToOtpSecretCreationAction: Action<null> | null = null;
    private continueToPasscodeConfirmationAction: Action<null> | null = null;
    private continueToPasscodeConfirmationRecoveryAction: Action<null> | null = null;
    private resendPasscodeAction: Action<null> | null = null;
    private verifyPasscodeAction: Action<VerifyPasscodeInputs> | null = null;
    private passwordLoginAction: Action<PasswordInputs> | null = null;
    private passwordRecoveryAction: Action<PasswordRecoveryInputs> | null = null;
    private thirdPartyOauthAction: Action<ThirdpartyOauthInputs> | null = null;
    private continueToLoginSecurityKeyAction: Action<null> | null = null;
    private otpCodeValidateAction: Action<OTPCodeInputs> | null = null;
    private trustDeviceAction: Action<null> | null = null;
    private exchangeTokenAction: Action<ExchangeTokenInputs> | null = null;
    private registerPasswordAction: Action<RegisterPasswordInputs> | null = null;
    private otpCodeVerifyAction: Action<OTPCodeInputs> | null = null;
    private loginCheckResult: LoginCheckApiResponse | null = null;

    // private beforeHandlerRef: CleanupFunc | null = null;
    private afterHandlerRef: CleanupFunc | null = null;
    private readonly appUrlOpenListener: PluginListenerHandle | null = null;
    private readonly pluginListerners: PluginListenerHandle[] = [];
    private readonly options = inject(HANKO_OPTIONS);
    private readonly envInjector = inject(EnvironmentInjector);

    // private readonly THIRDPARTY_REDIRECT_TRACKING_KEY = 'hanko_thirdparty_redirect_tracking';

    ngOnDestroy(): void {
        console.log('ngOnDestroy');
        // if (this.beforeHandlerRef != null) {
        //   this.beforeHandlerRef();
        // }
        if (this.afterHandlerRef != null) {
            this.afterHandlerRef();
        }
        if (this.appUrlOpenListener != null) {
            this.appUrlOpenListener
                .remove()
                .then(() => console.log('app url listener removed'));
        }

        for (const l of this.pluginListerners) {
            l.remove().then(() => console.log('listener removed'));
        }

        this.stopPasscodeResendCountdown();
    }

    ngOnInit(): void {
        console.log('ngOnInit');
        // this.beforeHandlerRef = this.hanko.instance.onBeforeStateChange(
        //   ({ state }) => {
        //     // this.isLoading = true;
        //     // this.stateName = null;
        //     console.log('onBeforeStateChange: ', state);
        //   }
        // );

        this.afterHandlerRef = this.hanko.instance.onAfterStateChange(
            async ({state}) => await this.handleStateChange(state)
        );

        this.initAsync().then();
    }

    async initAsync() {
        console.log('initAsync');

        if (Capacitor.isNativePlatform()) {
            const l1 = await InAppBrowser.addListener('browserClosed', () => {
                console.log('browserClosed', 'browser was closed. closed by user:', this.browserClosedByUser);
                this.zone.run(() => {
                    this.browserClosed = true;
            });
                if (this.browserClosedByUser) {
                    if (Capacitor.getPlatform().toLowerCase() === 'ios') {
                        this.reset();
                    }
                }
            });

            this.pluginListerners.push(l1);

            const l2 = await InAppBrowser.addListener(
                'browserPageNavigationCompleted',
                (data) => {
                    console.log(
                        'browserPageNavigationCompleted',
                        'browser page navigation was completed. ' + data.url
                    );
                }
            );
            this.pluginListerners.push(l2);

            const l3 = await InAppBrowser.addListener('browserPageLoaded', () => {
                console.log('browserPageLoaded', 'browser was loaded.');
            });
            this.pluginListerners.push(l3);

            const l4 = await App.addListener('appUrlOpen', async (data) => {
                console.log('appUrlOpen', data);

                if (data.url.includes('callback')) {
                    const callbackUrl = data.url;

                    this.browserClosedByUser = false;

                    await InAppBrowser.close();

                    this.zone.run(() => {
                        console.log('hanko-callback, check state');
                        this.doThirdPartyCallback(callbackUrl).then(
                            () => 'thirdparty callback finished'
                        );
                    });
                }
            });
            this.pluginListerners.push(l4);
        }

        const isLoggedIn = await this.hanko.checkIsLoggedIn();
        console.log('initAsync', 'isLoggedIn:', isLoggedIn);
        this.isLoggedIn = isLoggedIn;

        this.clearPage();

        if (!isLoggedIn) {
            console.log('initAsync', 'startlogin');

            const identifier = await this.hanko.getLoginIdentifier();
            this.formLoginIdentifier = identifier ?? '';

            await this.hanko.startLogin();
        } else {
            // console.log('initAsync', 'getUserData');
            // this.user = await this.hanko.getUserData();
            // await this.checkUserMfa(this.user);
        }
    }

    navigate(path: string) {
        return this.router.navigate([path]);
    }

    async restart() {
        console.log('restart');
        this.clearPage();

        // ThirdParty Tracking löschen beim Restart
        // this.clearThirdPartyRedirectTracking();

        if (this.isLoggedIn) {
            await this.hanko.logout();
        }
        this.isLoggedIn = false;
        this.isLoading = true;
        this.isStateChangeInProgress = false;
        return this.hanko.startLogin(true);
    }

    async logout() {
        console.log('logout');
        this.clearPage();
        await this.hanko.logout();
        this.isLoggedIn = false;
        await this.router.navigate([this.options.loginRoute], {
            replaceUrl: true,
        });
    }

    async reset(): Promise<void> {
        try {
            console.log('Starting application reset...');

            // 1. Lokale States löschen
            this.clearPage();

            // 2. Hanko logout durchführen
            if (this.isLoggedIn) {
                await this.hanko.logout();
            }

            // 3. ThirdParty Tracking explizit löschen (vor localStorage.clear)
            // this.clearThirdPartyRedirectTracking();

            // 4. LocalStorage löschen
            try {
                localStorage.clear();
                console.log('LocalStorage cleared');
            } catch (e) {
                console.warn('Failed to clear localStorage:', e);
            }

            // 5. SessionStorage löschen
            try {
                sessionStorage.clear();
                console.log('SessionStorage cleared');
            } catch (e) {
                console.warn('Failed to clear sessionStorage:', e);
            }

            // 6. Capacitor Preferences löschen
            try {
                await Preferences.clear();
                console.log('Capacitor Preferences cleared');
            } catch (e) {
                console.warn('Failed to clear Preferences:', e);
            }

            // 7. IndexedDB Datenbanken löschen (für localforage & andere)
            try {
                if ('indexedDB' in globalThis) {
                    const databases = await indexedDB.databases();
                    for (const db of databases) {
                        if (db.name) {
                            indexedDB.deleteDatabase(db.name);
                            console.log('Deleted IndexedDB:', db.name);
                        }
                    }
                }
            } catch (e) {
                console.warn('Failed to clear IndexedDB:', e);
            }

            // 8. Hanko Session Storage zurücksetzen
            try {
                this.hanko.instance.client.sessionTokenStorage.removeSessionToken();
            } catch (e) {
                console.warn('Failed to remove session token:', e);
            }

            // 9. Hanko Flow States explizit löschen
            try {
                await this.hanko.cleanStart();
            } catch (e) {
                console.warn('Failed to clean Hanko start:', e);
            }

            // 10. Nur im Browser: Cache & Service Worker löschen
            if (!Capacitor.isNativePlatform()) {
                // Browser Caches löschen
                try {
                    if ('caches' in globalThis) {
                        const cacheNames = await caches.keys();
                        await Promise.all(cacheNames.map(name => caches.delete(name)));
                        console.log('Browser caches cleared');
                    }
                } catch (e) {
                    console.warn('Failed to clear caches:', e);
                }

                // Service Worker deregistrieren
                try {
                    if ('serviceWorker' in navigator) {
                        const registrations = await navigator.serviceWorker.getRegistrations();
                        for (const registration of registrations) {
                            await registration.unregister();
                            console.log('Service Worker unregistered');
                        }
                    }
                } catch (e) {
                    console.warn('Failed to unregister service workers:', e);
                }
            }

            console.log('Application reset complete');

            // 11. Seite neu laden
            globalThis.location.reload();

        } catch (error) {
            console.error('Error during application reset:', error);
            // Trotzdem neu laden
            globalThis.location.reload();
        }
    }

    async doContinueWithLoginIdentifier(loginIdentifier: string | null) {
        this.updateEmailValidation(loginIdentifier);

        if (this.emailInvalid) {
            return;
        }

        await this.hanko.saveLoginIdentifier(loginIdentifier);

        const conti = async () => {
            if (this.continueWithLoginIdentifierAction != null) {
                const name = this.loginIdentifierInput?.name;

                if (name !== undefined && loginIdentifier != null) {
                    await this.continueWithLoginIdentifierAction.run({
                        [name]: loginIdentifier,
                    });
                }
            }
        };

        // wenn es keinen microsoft login gibt, dann keinen api check
        if (this.loginInitOptions?.microsoftEnabled === false) {
            await conti();
        } else {
            // api checken ob für diesen user ein microsoft login gemacht wird
            this.hanko.apiLoginCheck(loginIdentifier).subscribe(async (x) => {
                this.loginCheckResult = x;
                if (x.use_entra_id) {
                    this.entraInfo = x;
                    this.entraUser = loginIdentifier;
                    // login flow neu starten, aber nicht aus storage laden, also clean neustart
                    // bei der nächsten runde wird dann microsoft login durchgeführt
                    await this.hanko.startLogin(true);
                } else {
                    this.entraInfo = null;
                    await conti();
                }
            });
        }
    }

    async submitLoginIdentifier() {
        this.isStateChangeInProgress = true;
        this.markEmailTouched();
        if (this.emailInvalid) {
            this.isStateChangeInProgress = false;
            return;
        }

        await this.doContinueWithLoginIdentifier(this.formLoginIdentifier);

        this.isStateChangeInProgress = false;
    }

    markEmailTouched() {
        this.emailTouched = true;
        this.updateEmailValidation(this.formLoginIdentifier);
    }

    updateEmailValidation(value: string | null) {
        const val = (value ?? '').trim();

        if (val.length === 0) {
            this.emailError = 'required';
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(val)) {
            this.emailError = 'pattern';
            return;
        }

        this.emailError = null;
    }

    async doContinueWithMicrosoftLogin() {
        if (this.thirdPartyOauthAction !== undefined) {
            let redirectTo = globalThis.location.origin + '/callback';

            if (Capacitor.isNativePlatform()) {
                const appInfo = await App.getInfo();
                console.log('appInfo:', appInfo);
                redirectTo = appInfo.id + '://callback';
            }
            console.log('continueWithMicrosoftLogin:', redirectTo);
            this.thirdPartyRedirectUrl = redirectTo;

            const codeVerifier = await this.hanko.createCodeVerifier();

            const provider = 'microsoft';

            await this.thirdPartyOauthAction.run({
                provider,
                redirect_to: redirectTo,
                // @ts-ignore
                code_verifier: codeVerifier,
            });
        }
    }

    async doBack() {
        if (this.backAction !== null) {
            await this.backAction.run();
        }
    }

    async doSkip() {
        if (this.skipAction !== null) {
            await this.skipAction.run();
        }
    }

    async doPasswordLogin(password: string | null) {
        if (this.passwordLoginAction !== null && password !== null) {
            await this.passwordLoginAction.run({password});
        }
    }

    async doPasswordRecovery(password: string | null) {
        this.isStateChangeInProgress = true;
        if (this.passwordRecoveryAction !== null && password !== null) {
            await this.passwordRecoveryAction.run({
                new_password: password,
            });
            this.isStateChangeInProgress = false;
        } else {
            this.isStateChangeInProgress = false;
        }
    }

    markPasswordTouched() {
        this.passwordTouched = true;
        this.updatePasswordValidation(this.formPassword);
    }

    updatePasswordValidation(value: string | null) {
        const val = (value ?? '').trim();

        if (val.length === 0) {
            this.passwordError = 'required';
            return;
        }

        this.passwordError = null;
    }

    async doContinueToPasscodeConfirmation() {
        if (this.continueToPasscodeConfirmationAction !== null) {
            await this.continueToPasscodeConfirmationAction.run();
        }
    }

    async doContinueToPasscodeConfirmationRecovery() {
        if (this.continueToPasscodeConfirmationRecoveryAction !== null) {
            await this.continueToPasscodeConfirmationRecoveryAction.run();
        }
    }

    async doContinueToPasswordLogin() {
        if (this.continueToPasswordLoginAction !== null) {
            await this.continueToPasswordLoginAction.run();
        }
    }

    async doVerifyPasscode(passcode: string | null) {
        if (this.verifyPasscodeAction !== null && passcode !== null) {
            await this.verifyPasscodeAction.run({
                code: passcode,
            });
        }
    }

    markPasscodeTouched() {
        this.passcodeTouched = true;
        this.updatePasscodeValidation(this.formPasscode);
    }

    updatePasscodeValidation(value: string | null) {
        const val = (value ?? '').trim();

        if (val.length === 0) {
            this.passcodeError = 'required';
            return;
        }

        this.passcodeError = null;
    }

    async doResendPasscode() {
        if (this.resendPasscodeAction !== null) {
            await this.resendPasscodeAction.run();
        }
    }

    async doTrustDevice() {
        if (this.trustDeviceAction !== null) {
            await this.trustDeviceAction.run();
        }
    }

    async doContinueToLoginSecurityKey() {
        if (this.continueToLoginSecurityKeyAction !== null) {
            await this.continueToLoginSecurityKeyAction.run();
        }
    }

    async doContinueToLoginMethodChooser() {
        if (this.continueToLoginMethodChooserAction !== null) {
            await this.continueToLoginMethodChooserAction.run();
        }
    }

    async doOtpCodeValidate(otpCode: string | null) {
        this.isStateChangeInProgress = true;
        if (this.otpCodeValidateAction !== null && otpCode !== null) {
            await this.otpCodeValidateAction.run({
                otp_code: otpCode,
            });
            this.isStateChangeInProgress = false;
        } else {
            this.isStateChangeInProgress = false;
        }
    }

    markOtpCodeTouched() {
        this.otpCodeTouched = true;
        this.updateOtpCodeValidation(this.formOtpCode);
    }

    updateOtpCodeValidation(value: string | null) {
        const val = (value ?? '').trim();

        if (val.length === 0) {
            this.otpCodeError = 'required';
            return;
        }

        this.otpCodeError = null;
    }

    async doPasswordCreation(password: string | null) {
        this.isStateChangeInProgress = true;
        if (this.registerPasswordAction !== null && password !== null) {
            await this.registerPasswordAction.run({
                new_password: password,
            });
            this.isStateChangeInProgress = false;
        } else {
            this.isStateChangeInProgress = false;
        }
    }

    markPasswordCreationTouched() {
        this.passwordCreationTouched = true;
        this.updatePasswordCreationValidation(this.formPassword);
    }

    updatePasswordCreationValidation(value: string | null) {
        const val = (value ?? '').trim();

        const minLength = this.newPasswordInput?.min_length ?? 8;

        // Regeln prüfen
        this.passwordCreationRules.minLength = val.length >= minLength;
        this.passwordCreationRules.uppercase = /[A-Z]/.test(val);
        this.passwordCreationRules.lowercase = /[a-z]/.test(val);
        this.passwordCreationRules.number = /\d/.test(val);
        // Prüft, ob mindestens ein Sonderzeichen vorhanden ist (alles außer Buchstaben und Zahlen)
        this.passwordCreationRules.special = /[^A-Za-z\d]/.test(val);
        // Fehlermeldung setzen
        if (val.length === 0) {
            this.passwordCreationError = 'required';
            return;
        }

        const passwordRegex = new RegExp(String.raw`^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{${minLength},}$`);
        this.passwordCreationError = passwordRegex.test(val) ? null : 'pattern';
    }

    getPasswordCreationRuleClass(rule: keyof typeof this.passwordCreationRules) {
        const val = this.formPassword ?? '';

        if (!val) { return 'neutral'; }                             // noch nichts eingegeben
        if (this.passwordCreationRules[rule]) { return 'valid'; }   // erfüllt
        return 'invalid';                                           // nicht erfüllt
    }

    async doThirdPartyBrowserRun() {
        const browserUrl = this.thirdPartyBrowserUrl;
        if (browserUrl != null) {
            if (Capacitor.isNativePlatform()) {
                // await Browser.addListener('browserFinished', () => {
                //   console.log(`login: cap: browserFinished: ${browserUrl}`);
                //
                //   Browser.close().then(() => console.log('login: cap: browser closed'));
                // });
                //
                // await Browser.addListener('browserPageLoaded', () => {
                //   console.log(`login: cap: browserPageLoaded: ${browserUrl}`);
                // });

                // await Browser.open({
                //   browserUrl: browserUrl.toString(),
                //   presentationStyle: 'popover',
                // });

                // await InAppBrowser.openInWebView({
                //   browserUrl: browserUrl.toString(),
                //   options: DefaultWebViewOptions,
                //   customHeaders: {},
                // });

                await InAppBrowser.openInSystemBrowser({
                    url: browserUrl,
                    options: DefaultSystemBrowserOptions,
                });
            } else {
                globalThis.location.assign(browserUrl);
            }
        }
    }

    async doCreateOtpSecret() {
        if (this.continueToOtpSecretCreationAction !== null) {
            await this.continueToOtpSecretCreationAction.run();
        }
    }

    async doOtpCodeVerify(otpCode: string | null) {
        this.isStateChangeInProgress = true;
        if (this.otpCodeVerifyAction !== null && otpCode !== null) {
            await this.otpCodeVerifyAction.run({
                otp_code: otpCode,
            });
            this.isStateChangeInProgress = false;
        } else {
            this.isStateChangeInProgress = false;
        }
    }

    markMfaOtpCodeTouched() {
        this.mfaOtpCodeTouched = true;
        this.updateMfaOtpCodeValidation(this.formOtpCode);
    }

    updateMfaOtpCodeValidation(value: string | null) {
        const val = (value ?? '').trim();

        if (val.length === 0) {
            this.mfaOtpCodeError = 'required';
            return;
        }

        this.mfaOtpCodeError = null;
    }

    async doCreateOtpAfterSuccess() {
        console.log('doCreateOtpAfterSuccess');

        const pflow = await this.hanko.instance.createState('profile');
        this.stateObject = pflow.serialize();

        if (pflow.name === 'profile_init') {
            console.log('doCreateOtpAfterSuccess', 'profile_init');
            if (pflow.actions.continue_to_otp_secret_creation?.enabled) {
                console.log(
                    'doCreateOtpAfterSuccess',
                    'actions.continue_to_otp_secret_creation.run'
                );

                await pflow.actions.continue_to_otp_secret_creation.run();
            }
        }

        // if (pflow.name === 'mfa_otp_secret_creation') {
        //   console.log('doCreateOtpAfterSuccess', 'mfa_otp_secret_creation', 'actions.continue_to_otp_secret_creation.run');
        //   this.handleMfaOtpSecretCreationState(pflow);
        // }
    }

    private async doThirdPartyCallback(
        callbackurl: string | null
    ): Promise<boolean> {
        console.log('doThirdPartyCallback:', callbackurl);

        if (callbackurl == null) { return false; }

        const url = new URL(callbackurl);
        const searchParams = url.searchParams;
        const token = searchParams.get('hanko_token');
        const error = searchParams.get('error');

        const updateUrl = (paramsToDelete: string[]) => {
            paramsToDelete.forEach((param) => searchParams.delete(param));
            const newSearch = searchParams.toString()
                ? `?${searchParams.toString()}`
                : '';
            history.replaceState(null, '', `${globalThis.location.pathname}${newSearch}`);
        };

        console.log('handleThirdPartyState', 'token', token, 'error', error);

        if (token && token?.length > 0) {
            updateUrl(['hanko_token']);
            const codeVerifier = await this.hanko.getCodeVerifier();

            if (this.exchangeTokenAction != null) {
                await this.exchangeTokenAction.run({
                    token,
                    // @ts-ignore
                    code_verifier: codeVerifier,
                });
            }

            return true;
        }

        if (error && error?.length > 0) {
            const errorCode =
                error === 'access_denied'
                    ? 'third_party_access_denied'
                    : 'technical_error';
            const message = searchParams.get('error_description');

            updateUrl(['error', 'error_description']);

            this.error = {
                message: message ?? '',
                code: errorCode,
            };

            if (this.backAction != null) {
                await this.backAction.run(undefined, {
                    dispatchAfterStateChangeEvent: false,
                });
                return true;
            }
        }

        return false;
    }

    private clearPage() {
        console.log('clearPage');
        // this.isLoggedIn = false;
        this.isLoading = false;
        this.stateName = null;
        this.stateObject = null;
        this.error = null;
        this.zone.run(() => {
            this.browserClosed = false;
        });
        this.browserClosedByUser = true;

        this.clearOptions();

        this.emailTouched = false;
        this.emailError = null;
        this.passwordTouched = false;
        this.passwordError = null;
        this.passwordCreationTouched = false;
        this.passwordCreationError = null;
        this.passwordCreationRules = {
            minLength: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false,
        };

        // this.loginIdentifier = '';

        this.formPasscode = '';
        this.formPassword = '';
        this.formOtpCode = '';

        this.user = null;
        this.thirdPartyBrowserUrl = null;
        this.thirdPartyRedirectUrl = null;
        this.entraInfo = null;
    }

    private clearOptions() {
        this.loginMethodChooserOptions = null;
        this.passcodeConfirmationsOptions = null;
        this.loginInitOptions = null;
        this.loginPasswordOptions = null;
        this.loginOtpOptions = null;
        this.deviceTrustOptions = null;
        this.mfaMethodChooserOptions = null;
        this.mfaSecretCreationOptions = null;
        this.createPasswordOptions = null;
        this.successOptions = null;
        this.stopPasscodeResendCountdown();
    }

    private async checkUserMfa(user: User | null) {
        if (user == null) {
            user = await this.hanko.getUserData();
        }

        if (!user) { return false; }

        const tenantsNeedsMfa = tenantNeedsTrustedUser(user);
        // console.log('tenantsNeedsMfa', tenantsNeedsMfa);
        const userHasMfa = userHasMfaEnabled(user);
        // console.log('userHasMfa', userHasMfa);
        const userHasMicrosoft = userHasMicrosoftLinked(user);
        // console.log('userHasMicrosoft', userHasMicrosoft);

        const mfaCreationRequired = tenantsNeedsMfa && !(userHasMfa || userHasMicrosoft);
        // console.log('mfaCreationRequired', mfaCreationRequired);
        this.successOptions = {
            userMfaCreationRequired: mfaCreationRequired,
        };

        // falls mit mfa alles ok ist dann
        return mfaCreationRequired === false;
    }

    private async handleStateChange(state: AnyState) {
        {
            // console.log('onAfterStateChange: ', state);
            console.log('onAfterStateChange', 'state:', state.name);
            state.saveToLocalStorage();

            this.stateName = state.name;
            this.error = state.error ?? null;
            this.stateObject = state.serialize();

            // Alte Optionen löschen, damit keine Mischzustände entstehen
            this.clearOptions();

            switch (state.name) {
                case 'error': {
                    this.error = state.error ?? null;
                    break;
                }
                case 'login_init': {
                    await this.handleLoginInitState(state);
                    break;
                }
                case 'login_method_chooser': {
                    await this.handleLoginMethodChooserState(state);
                    break;
                }
                case 'passcode_confirmation': {
                    this.handlePasscodeConfirmationState(state);
                    break;
                }
                case 'login_password': {
                    this.handleLoginPasswordState(state);
                    break;
                }
                case 'login_password_recovery': {
                    this.handleLoginPasswordRecoveryState(state);
                    break;
                }
                case 'login_otp': {
                    this.handleLoginOtpState(state);
                    break;
                }
                case 'device_trust': {
                    this.handleDeviceTrustState(state);
                    break;
                }
                case 'thirdparty': {
                    await this.handleThirdPartyState(state);
                    break;
                }
                case 'mfa_method_chooser': {
                    await this.handleMfaMethodChooserState(state);
                    break;
                }
                case 'password_creation': {
                    this.handlePasswordCreationState(state);
                    break;
                }
                case 'mfa_otp_secret_creation': {
                    this.handleMfaOtpSecretCreationState(state);
                    break;
                }
                case 'success': {
                    await this.handleSuccessState(state);
                    break;
                }
                default:
                    this.isLoading = true;
            }
        }
    }

    private async handleSuccessState(state: State<'success'>) {
        console.log('handleSuccess');
        this.isLoggedIn = true;
        this.user = state.payload?.user ?? null;

        // Login erfolgreich, ThirdParty Tracking löschen
        // this.clearThirdPartyRedirectTracking();

        await this.hanko.clearLoginIdentifier();

        if (this.options.successFn) {
            await this.envInjector.runInContext(() => this.options.successFn(state.payload));
        }

        // await this.router.navigate([this.options.postLoginRoute], {
        //     replaceUrl: true,
        //     onSameUrlNavigation: 'reload',
        // })

        // Optionen werden durch clearOptions() gelöscht, müssen aber für checkUserMfa verfügbar sein bzw. dort gesetzt werden.
        // checkUserMfa setzt successOptions. Das ist korrekt.

        // const suc = await this.checkUserMfa(state.payload?.user ?? null);
        // if (suc) {
        // await this.router.navigate([this.options.postLoginRoute], {
        //     replaceUrl: true,
        //     onSameUrlNavigation: 'reload',
        // })
        // }
    }

    private handleDeviceTrustState(state: State<'device_trust'>) {
        this.backAction = state.actions.back ?? null;
        this.skipAction = state.actions.skip ?? null;
        this.trustDeviceAction = state.actions.trust_device ?? null;

        this.deviceTrustOptions = {
            backEnabled: state.actions.back?.enabled === true,
            skipEnabled: state.actions.skip?.enabled === true,
            trustDeviceEnabled: state.actions.trust_device?.enabled === true,
        };
    }

    private handleLoginOtpState(state: State<'login_otp'>) {
        this.continueToLoginSecurityKeyAction =
            state.actions.continue_to_login_security_key ?? null;

        this.otpCodeValidateAction = state.actions.otp_code_validate;

        this.otpCodeInput = state.actions.otp_code_validate.inputs.otp_code;

        this.loginOtpOptions = {
            securityKeyEnabled:
                state.actions.continue_to_login_security_key?.enabled === true,
            codeValidateEnabled: state.actions.otp_code_validate?.enabled === true,
        };
    }

    private handleLoginPasswordState(state: State<'login_password'>) {
        this.continueToLoginMethodChooserAction =
            state.actions.continue_to_login_method_chooser;

        this.backAction = state.actions.back;
        this.continueToPasscodeConfirmationRecoveryAction = state.actions.continue_to_passcode_confirmation_recovery;
        this.passwordLoginAction = state.actions.password_login;
        this.passwordInput = state.actions.password_login.inputs.password;

        this.loginPasswordOptions = {
            backEnabled: state.actions.back.enabled,
            chooserEnabled: state.actions.continue_to_login_method_chooser.enabled,
        };
    }

    private handleLoginPasswordRecoveryState(state: State<'login_password_recovery'>) {
        this.formPassword = null;

        this.newPasswordInput = state.actions.password_recovery.inputs.new_password;

        this.passwordRecoveryAction = state.actions.password_recovery ?? null;

    }

    private async handleLoginMethodChooserState(
        state: State<'login_method_chooser'>
    ) {
        this.backAction = state.actions.back;

        this.continueToPasswordLoginAction =
            state.actions.continue_to_password_login ?? null;

        this.continueToPasscodeConfirmationAction =
            state.actions.continue_to_passcode_confirmation ?? null;

        this.loginMethodChooserOptions = {
            passwordEnabled:
                state.actions.continue_to_password_login?.enabled === true,
            passcodeEnabled:
                state.actions.continue_to_passcode_confirmation?.enabled === true,
            backEnabled: state.actions.back.enabled,
        };

        if (this.loginMethodChooserOptions.passwordEnabled) {
            await this.doContinueToPasswordLogin();
        }
    }

    private async handleMfaMethodChooserState(state: State<'mfa_method_chooser'>) {
        this.skipAction = state.actions.skip ?? null;
        this.backAction = state.actions.back ?? null;

        console.log('this.formLoginIdentifier', this.formLoginIdentifier);

        if (!this.loginCheckResult) {
            console.log('this.loginCheckResult noch nicht da, hole von api');
            this.loginCheckResult = await lastValueFrom(this.hanko.apiLoginCheck(this.formLoginIdentifier));
        }

        console.log('this.loginCheckResult', this.loginCheckResult);

        const mfaRequired = this.loginCheckResult?.trusted_required;

        console.log('mfaRequired', mfaRequired);

        this.continueToOtpSecretCreationAction =
            state.actions.continue_to_otp_secret_creation ?? null;

        this.mfaMethodChooserOptions = {
            skipEnabled: state.actions.skip?.enabled === !mfaRequired,
            backEnabled: state.actions.back?.enabled === true,
            continueToOtpSecretEnabled:
                state.actions.continue_to_otp_secret_creation?.enabled === true,
        };
    }

    private handleMfaOtpSecretCreationState(
        state: State<'mfa_otp_secret_creation'>
    ) {
        this.formOtpCode = null;
        this.backAction = state.actions.back ?? null;

        this.otpCodeVerifyAction = state.actions.otp_code_verify ?? null;

        if (state.actions.otp_code_verify) {
            this.mfaOtpCodeInput = state.actions.otp_code_verify.inputs.otp_code;
        }

        this.mfaSecretCreationOptions = {
            backEnabled: state.actions.back?.enabled === true,
            continueToCodeVerifyEnabled:
                state.actions.otp_code_verify?.enabled === true,
            otp_secret: state.payload?.otp_secret,
            otp_image_source: state.payload?.otp_image_source,
            // otp_image: this.sanitizer.bypassSecurityTrustUrl()
        };
    }

    private handlePasswordCreationState(state: State<'password_creation'>) {
        this.formPassword = null;
        this.skipAction = state.actions.skip ?? null;
        this.backAction = state.actions.back ?? null;

        this.newPasswordInput = state.actions.register_password.inputs.new_password;

        this.registerPasswordAction = state.actions.register_password ?? null;

        this.createPasswordOptions = {
            skipEnabled: state.actions.skip?.enabled === true,
            backEnabled: state.actions.back?.enabled === true,
            registerPasswordEnabled:
                state.actions.register_password?.enabled === true,
        };
    }

    private async handleLoginInitState(state: State<'login_init'>) {
        this.isLoggedIn = false;
        this.user = null;
        this.emailTouched = false;
        this.emailError = null;
        this.updateEmailValidation(this.formLoginIdentifier);

        this.continueWithLoginIdentifierAction =
            state.actions.continue_with_login_identifier ?? null;

        this.thirdPartyOauthAction = state.actions.thirdparty_oauth ?? null;

        const continueWithLoginIdentifierInputs = (
            state.actions
                .continue_with_login_identifier as Action<ContinueWithLoginIdentifierInputs>
        ).inputs;

        this.loginIdentifierInput = (continueWithLoginIdentifierInputs.email ||
            continueWithLoginIdentifierInputs.identifier) as Input<string>;

        let microsoftEnabled = false;

        if (state.actions.thirdparty_oauth?.enabled === true) {
            const thirdPartyInputs = (
                state.actions.thirdparty_oauth as Action<ThirdpartyOauthInputs>
            ).inputs;

            const allowedValues = thirdPartyInputs.provider.allowed_values;
            // console.log('thirdparty allowedValues:', allowedValues);
            if (allowedValues) {
                const msix = allowedValues.findIndex((x) => x.value === 'microsoft');
                microsoftEnabled = msix > -1;
            }
        }

        console.log('microsoftEnabled:', microsoftEnabled);

        this.loginInitOptions = {
            rememberMe: state.actions.remember_me?.enabled === true,
            continueWithLoginIdentifierEnabled:
                state.actions.continue_with_login_identifier?.enabled === true,
            microsoftEnabled,
        };

        if (!microsoftEnabled) {
            // falls es kein microsoft login gibt ist entra egal
            this.entraInfo = null;
        }

        if (this.entraInfo?.use_entra_id === true && !state.error) {
            await this.doContinueWithMicrosoftLogin();
        }
    }

    private handlePasscodeConfirmationState(
        state: State<'passcode_confirmation'>
    ) {
        // if (this.entraInfo?.useEntra === true) {
        //   this.continueWithMicrosoftLogin().then();
        //   return;
        // }

        this.backAction = state.actions.back;

        this.resendPasscodeAction = state.actions.resend_passcode;
        this.verifyPasscodeAction = state.actions.verify_passcode;

        this.passcodeInput = state.actions.verify_passcode.inputs.code;

        // Speichere resend_after wenn ein Rate-Limit-Fehler vorliegt
        if (state.payload && 'resend_after' in state.payload) {
            this.passcodeResendAfter = (state.payload as any).resend_after;
            this.startPasscodeResendCountdown();
        } else {
            this.passcodeResendAfter = null;
            this.stopPasscodeResendCountdown();
        }

        this.passcodeConfirmationsOptions = {
            resendEnabled: state.actions.resend_passcode.enabled,
            backEnabled: state.actions.back.enabled,
        };
    }

    private startPasscodeResendCountdown() {
        this.stopPasscodeResendCountdown();
        
        this.passcodeResendCountdownInterval = setInterval(() => {
            if (this.passcodeResendAfter !== null && this.passcodeResendAfter > 0) {
                this.passcodeResendAfter--;
                
                if (this.passcodeResendAfter === 0) {
                    // Countdown ist abgelaufen, Fehler clearen
                    this.error = null;
                    this.stopPasscodeResendCountdown();
                }
            }
        }, 1000);
    }

    private stopPasscodeResendCountdown() {
        if (this.passcodeResendCountdownInterval !== null) {
            clearInterval(this.passcodeResendCountdownInterval);
            this.passcodeResendCountdownInterval = null;
        }
    }

    // private getThirdPartyRedirectTracking(): {url: string; timestamp: number; count: number} | null {
    //     try {
    //         const data = localStorage.getItem(this.THIRDPARTY_REDIRECT_TRACKING_KEY);
    //         if (data) {
    //             return JSON.parse(data);
    //         }
    //     } catch (e) {
    //         console.warn('Failed to read thirdparty redirect tracking:', e);
    //     }
    //     return null;
    // }

    // private setThirdPartyRedirectTracking(url: string): void {
    //     try {
    //         const existing = this.getThirdPartyRedirectTracking();
    //
    //         // Wenn dieselbe URL, Counter erhöhen, sonst neu starten
    //         if (existing && existing.url === url) {
    //             localStorage.setItem(
    //                 this.THIRDPARTY_REDIRECT_TRACKING_KEY,
    //                 JSON.stringify({
    //                     url,
    //                     timestamp: Date.now(),
    //                     count: existing.count + 1
    //                 })
    //             );
    //             console.log('ThirdParty redirect tracking updated, count:', existing.count + 1);
    //         } else {
    //             localStorage.setItem(
    //                 this.THIRDPARTY_REDIRECT_TRACKING_KEY,
    //                 JSON.stringify({
    //                     url,
    //                     timestamp: Date.now(),
    //                     count: 1
    //                 })
    //             );
    //             console.log('ThirdParty redirect tracking started for new URL');
    //         }
    //     } catch (e) {
    //         console.warn('Failed to set thirdparty redirect tracking:', e);
    //     }
    // }

    // private clearThirdPartyRedirectTracking(): void {
    //     try {
    //         localStorage.removeItem(this.THIRDPARTY_REDIRECT_TRACKING_KEY);
    //         console.log('ThirdParty redirect tracking cleared');
    //     } catch (e) {
    //         console.warn('Failed to clear thirdparty redirect tracking:', e);
    //     }
    // }

    private async handleThirdPartyState(state: State<'thirdparty'>) {
        this.exchangeTokenAction = state.actions.exchange_token ?? null;
        this.backAction = state.actions.back ?? null;

        // bei capacitor wird der callback über den appurl geregelt
        const locationUrl = Capacitor.isNativePlatform() ? null : window.location.href;
        const isCallBackHandled = await this.doThirdPartyCallback(locationUrl);

        if (isCallBackHandled) {
            // Callback erfolgreich verarbeitet, Tracking löschen
            // this.clearThirdPartyRedirectTracking();
            return;
        }

        if (state.payload && state.payload.redirect_url) {
            const redirectUrl = state.payload.redirect_url;

            // Prüfe, ob wir in einem Loop sind
            // const tracking = this.getThirdPartyRedirectTracking();
            // if (tracking) {
            //     // Wenn dieselbe URL bereits mindestens 1x versucht wurde, könnte es ein Loop sein
            //     if (tracking.url === redirectUrl && tracking.count >= 1) {
            //         console.warn('ThirdParty redirect loop detected! URL:', redirectUrl, 'Count:', tracking.count);
            //         console.warn('Preventing endless loop - initiating clean restart');
            //
            //         // Tracking löschen
            //         // this.clearThirdPartyRedirectTracking();
            //
            //         // Fehler anzeigen
            //         this.error = {
            //             code: 'thirdparty_loop_detected',
            //             message: 'Ein Fehler ist beim Login mit dem externen Anbieter aufgetreten. Bitte versuchen Sie es erneut.'
            //         };
            //
            //         // Clean Start durchführen
            //         await this.hanko.cleanStart();
            //
            //         // Zurück zum Login
            //         await this.restart();
            //
            //         return;
            //     }
            //
            //     // Wenn es eine andere URL ist, alte löschen
            //     if (tracking.url !== redirectUrl) {
            //         console.log('ThirdParty redirect URL changed, resetting tracking');
            //         // this.clearThirdPartyRedirectTracking();
            //     }
            // }

            // Tracking aktualisieren VOR der Weiterleitung
            // this.setThirdPartyRedirectTracking(redirectUrl);

            const url = new URL(redirectUrl);

            // nur bei microsoft
            if (url.host.includes('microsoftonline')) {
                const sp = url.searchParams;

                const updateOrDelete = (name: string, value: string) => {
                    if (sp.has(name)) {
                        sp.set(name, value);
                    } else {
                        sp.append(name, value);
                    }
                };

                if (sp.has('prompt')) {
                    sp.delete('prompt');
                }
                // updateOrDelete('prompt', 'select_account');

                if (this.entraUser !== null) {
                    updateOrDelete('login_hint', this.entraUser);
                }
                if (
                    this.entraInfo?.use_entra_id === true &&
                    this.entraInfo.entra_id_domain
                ) {
                    updateOrDelete('domain_hint', this.entraInfo.entra_id_domain);
                }
            }

            console.log('thirdPartyBrowserUrl:', url);
            this.thirdPartyBrowserUrl = url.toString();

            // wird wird aktuell gewartet auf doThirdPartyBrowserRun
            // TODO: den browser run direkt ausführen und dann den token    
            await this.doThirdPartyBrowserRun();
        } else {
            console.log('no redirect url');
            if (state.actions.back) {
                await state.actions.back.run();
            }
        }
    }

    protected async copyToClipboard(text: string | undefined) {
        if (!text) {
            return;
        }
        try {
            await Clipboard.write({
                string: text,
            });
            console.log('Text copied to clipboard:', text);
        } catch (error) {
            console.error('Failed to copy text to clipboard:', error);
        }
    }

    showMfaSetup() {
        this.showMfaHelp = false;
    }

    backToMfaHelp() {
        this.showMfaHelp = true;
    }
}
